import { PlaneLanding, PlaneTakeoff } from "lucide-react";
import Router, { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

import { ROUTES } from "shared/constants";
import { addAirportDestinations } from "shared/data/actions/destinations";
import {
  startUniversalLoading,
  stopUniversalLoading,
} from "shared/data/actions/loader";
import { setTransportCompanies } from "shared/data/actions/search";
import { AppState } from "shared/data/reducers";
import golApi from "shared/gol-api";
import { parseTransportCompanies, scrollTo } from "shared/lib/functions";
import {
  parseCodebookObject,
  parseFlightObjects,
} from "shared/lib/objectParsers";
import { setInitialRequestorConfig } from "shared/lib/requestorFunctions";
import { formattedMessageParams } from "shared/messages";
import Logger from "shared/services/Logger";

import BackToHomepageLink from "@components/BestOffer/BackToHomepageLink";
import FlightBox from "@components/BestOffer/FlightBox";
import {
  getFlightPassengerCount,
  getFlightPricePerPassenger,
  getFlightTotalPrice,
} from "@components/BestOffer/flightPricing";
import {
  Button,
  PriceFooter,
  RadioGroup,
  SectionTitle,
} from "@components/DesignSystem";
import DesktopEmptyDiv from "@components/UI/DesktopEmptyDiv";
import FormattedMessage from "@components/UI/FormattedMessage";
import SearchSteps from "@components/UI/Header/SearchSteps";
import IconChevronLeft from "@components/UI/Icons/ChevronLeft";
import StandardLayout from "@components/UI/Layouts/StandardLayout";
import useModalAlert from "@components/UI/useModalAlert";
import PageSeo from "@components/Utils/PageSeo";

import { pageDataLayer } from "@lib/dataLayers";

const BestOfferResults: any = () => {
  const router = useRouter();
  const intl = useIntl();
  const dispatch = useDispatch();

  const [state, setState] = useState({
    flights: [],
    oneWay: "",
    returnWay: "",
    oneWayTouched: false,
    returnWayTouched: false,
    isReturnSearch: false,
    codebook: null,
  });

  const [ModalAlert, showError] = useModalAlert({});

  const { user, currentLanguage } = useSelector((appState: AppState) => {
    return {
      user: appState.user,
      currentLanguage: appState.requestorConfig.currentLanguage,
    };
  });

  useEffect(() => {
    (async () => {
      scrollTo(".best-offer-page-heading");
      dispatch(startUniversalLoading());
      let response;
      const alternativeCurrency =
        typeof router.query.currency === "string"
          ? router.query.currency
          : undefined;
      try {
        response = await golApi.searchSpecialoffers(router.query.reference, {
          alternativeCurrency,
        });
      } catch (e) {
        Logger.error("API error", e);
        showError(
          intl.formatMessage({
            id: "Alert.somethingWentWrong",
          }),
          false,
          () => Router.push(ROUTES.HOME)
        );
        dispatch(stopUniversalLoading());
        return;
      }

      if (response?.success === false) {
        showError(
          response?.errorMsg ||
            intl.formatMessage({
              id: "Alert.somethingWentWrong",
            }),
          false,
          () => Router.push(ROUTES.HOME)
        );
        dispatch(stopUniversalLoading());
        return;
      }

      const flights = Array.isArray(response.data?.FlightOffers?.FlightOffer)
        ? [...response.data.FlightOffers.FlightOffer]
        : [];
      const codebook = { ...response.codebook };

      const parsedFlights = parseFlightObjects(flights);
      const oTransportCompanies = parseTransportCompanies(codebook);
      dispatch(addAirportDestinations(codebook?.Airports.Airport));
      dispatch(setTransportCompanies(oTransportCompanies));

      const parsedCodebook = parseCodebookObject(codebook);
      const isReturnSearch = !!parsedFlights.find((flight) => flight.returnWay);

      setState((currentState) => ({
        ...currentState,
        flights: [...parsedFlights],
        codebook: parsedCodebook,
        isReturnSearch,
      }));

      dispatch(stopUniversalLoading());
    })();
    pageDataLayer("bestOfferResults", user);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, intl, router.query.reference]);

  const handleReservation = () => {
    if (state.isReturnSearch && !(state.oneWay && state.returnWay)) {
      alert("Musíte vybrat cestu tam a zpět");
      return;
    }
    const refs = [state.oneWay, state.returnWay].filter((el) => el);
    const selectedLanguage =
      typeof router.query.lang === "string" && router.query.lang
        ? router.query.lang
        : currentLanguage;
    const selectedCurrency =
      typeof router.query.currency === "string" ? router.query.currency : null;

    const refsParams = refs.map((ref) => `refs=${encodeURIComponent(ref)}`);
    const otherParams = [`isSpecialOffer=${encodeURIComponent("true")}`];
    if (selectedLanguage) {
      otherParams.push(`lang=${encodeURIComponent(selectedLanguage)}`);
    }
    if (selectedCurrency) {
      otherParams.push(`currency=${encodeURIComponent(selectedCurrency)}`);
    }
    const allParams = [...refsParams, ...otherParams].join("&");

    const fullUrl = `${window.location.origin}${ROUTES.RESULT}?${allParams}`;

    window.location.href = fullUrl;
  };

  const selectOneWayFlight = (reference) =>
    setState((currentState) => ({
      ...currentState,
      oneWay: reference,
      oneWayTouched: true,
    }));

  const selectReturnWayFlight = (reference) =>
    setState((currentState) => ({
      ...currentState,
      returnWay: reference,
      returnWayTouched: true,
    }));

  const getReturnFlights = () =>
    state.flights.reduce((acc, currentValue) => {
      const exists = acc.find(
        (el) => el?.returnWay?.Reference === currentValue?.returnWay?.Reference
      );
      if (exists === undefined) acc.push(currentValue);
      return acc;
    }, []);

  let returnFlights = [];

  if (state.flights[0]?.returnWay !== undefined) {
    returnFlights = getReturnFlights();
  }
  let flights = [];
  if (state.flights[0] !== undefined) {
    flights = state.flights.reduce(
      (acc, currentValue) => {
        const exists = acc.find(
          (el) => el.oneWay.Reference === currentValue.oneWay.Reference
        );
        if (exists === undefined) acc.push(currentValue);
        return acc;
      },
      [state.flights[0]]
    );
  }

  useEffect(() => {
    if (!state.flights.length) {
      return;
    }

    setState((currentState) => ({
      ...currentState,
      ...(currentState.flights.length === 1 &&
        !currentState.oneWayTouched && {
          oneWayTouched: true,
          oneWay: currentState.flights[0].oneWay.Reference,
        }),
      ...(returnFlights.length === 1 &&
        !currentState.returnWayTouched && {
          returnWayTouched: true,
          returnWay: returnFlights[0].returnWay?.Reference,
        }),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.flights]);

  const { resultsTitle } = defineMessages({
    resultsTitle: formattedMessageParams("BestOffers.pickFlight"),
  });
  const useAlternativeCurrency =
    typeof router.query.currency === "string" &&
    router.query.currency.length > 0;
  const selectedOneWayFlight = state.oneWay
    ? flights.find((flight) => flight.oneWay.Reference === state.oneWay)?.oneWay
    : undefined;
  const selectedReturnWayFlight = state.returnWay
    ? returnFlights.find(
        (flight) => flight.returnWay?.Reference === state.returnWay
      )?.returnWay
    : undefined;
  const selectedFlightForPricing = state.isReturnSearch
    ? selectedOneWayFlight && selectedReturnWayFlight
      ? selectedReturnWayFlight
      : undefined
    : selectedOneWayFlight;
  const selectedPassengerCount = getFlightPassengerCount(
    selectedFlightForPricing,
    useAlternativeCurrency
  );
  const selectedPrice = getFlightPricePerPassenger(
    selectedFlightForPricing,
    useAlternativeCurrency
  );
  const selectedTotalPrice =
    selectedPassengerCount > 1
      ? getFlightTotalPrice(selectedFlightForPricing, useAlternativeCurrency)
      : null;
  const goBack = () => Router.back();

  return (
    <>
      <ModalAlert />
      <div className="container best-offer-page best-offer-results-page">
        <div className="special-offers-back-to-homepage-link-wrapper">
          <BackToHomepageLink />
        </div>
        <h1 className="best-offer-page-heading">
          {intl.formatMessage(resultsTitle)}
        </h1>
        <PageSeo
          title={{
            defaultTitle: intl.formatMessage(resultsTitle),
          }}
        />
        <div className="best-offer-results-steps">
          <div className="best-offer-results-steps-row">
            <button
              className="button-result-back best-offer-results-back-button"
              onClick={goBack}
              type="button"
            >
              <span className="label-button-back-result bold">
                <span className="icon-button-back-result-desktop">
                  <IconChevronLeft size={20} />
                </span>
                <FormattedMessage id="SearchSteps.back" />
              </span>
            </button>
            <button
              aria-label={intl.formatMessage({ id: "SearchSteps.back" })}
              className="icon-button-back-result-mobile best-offer-results-back-icon"
              onClick={goBack}
              type="button"
            >
              <IconChevronLeft size={25} />
            </button>
            <SearchSteps page="bestOfferResults" />
            <DesktopEmptyDiv styles={{ width: "60px" }} />
          </div>
        </div>
        <section className="best-offer-results-section">
          <SectionTitle icon={<PlaneTakeoff aria-hidden="true" size={24} />}>
            <FormattedMessage id="BestOffers.wayThere" />
          </SectionTitle>
          <div className="best-offer-instruction-text">
            <FormattedMessage id="BestOffers.selectRequiredFlight" />
          </div>
          <RadioGroup
            className="best-offer-results-way-there"
            onValueChange={selectOneWayFlight}
            tabIndex={-1}
            value={state.oneWay || ""}
          >
            {flights.map((flight, index) => (
              <FlightBox
                key={flight.oneWay.Reference + index}
                flight={flight.oneWay}
                codebook={state.codebook}
                direction="departure"
                selectFlight={selectOneWayFlight}
                selected={flight.oneWay.Reference === state.oneWay}
                touched={state.oneWayTouched}
              />
            ))}
          </RadioGroup>
        </section>
        {returnFlights.length !== 0 && (
          <section className="best-offer-results-section best-offer-results-return-section">
            <SectionTitle icon={<PlaneLanding aria-hidden="true" size={24} />}>
              <FormattedMessage id="BestOffers.wayBack" />
            </SectionTitle>
            <div className="best-offer-instruction-text">
              <FormattedMessage id="BestOffers.selectRequiredFlight" />
            </div>
            <RadioGroup
              className="best-offer-results-way-back"
              onValueChange={selectReturnWayFlight}
              tabIndex={-1}
              value={state.returnWay || ""}
            >
              {returnFlights.map((flight, index) => (
                <FlightBox
                  key={flight.returnWay?.Reference + index}
                  flight={flight.returnWay}
                  codebook={state.codebook}
                  direction="return"
                  selectFlight={selectReturnWayFlight}
                  selected={flight.returnWay?.Reference === state.returnWay}
                  touched={state.returnWayTouched}
                />
              ))}
            </RadioGroup>
          </section>
        )}

        <PriceFooter
          className="best-offer-promo-footer"
          action={
            <Button
              className="best-offer-select-flights"
              onClick={handleReservation}
              size="lg"
            >
              <FormattedMessage id="BestOffers.continueBooking" />
            </Button>
          }
          customCurrency={
            typeof router.query.currency === "string"
              ? router.query.currency
              : undefined
          }
          price={selectedPrice}
          priceLabel={<FormattedMessage id="BestOffers.perOnePassenger" />}
          totalLabel={
            selectedPassengerCount > 1 ? (
              <>
                <FormattedMessage id="BestOffers.allPassengers" />:
              </>
            ) : null
          }
          totalPrice={selectedTotalPrice}
        />
      </div>
    </>
  );
};

BestOfferResults.getInitialProps = async (initialProps) => {
  try {
    const { domain } = await setInitialRequestorConfig(initialProps);
    return { domain };
  } catch (e) {
    Logger.error("Error Initial. ", e);
    return {};
  }
};

BestOfferResults.getLayout = (page, nonce) => (
  <StandardLayout
    nonce={nonce}
    hideFooterRights
    hideSearchForm
    showCondensedHeaderLogoStripe
  >
    {page}
  </StandardLayout>
);

export default BestOfferResults;
